"use client";

import { useState } from "react";
import { useUser } from "@clerk/nextjs";
import { useNativeSignIn } from "@/hooks/useNativeSignIn";
import {
  Button,
  Box as MantineBox,
  Text as MantineText,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconX } from "@tabler/icons-react";
import { useFiltersStore } from "@/stores/filtersStore";
import { useSearchContext } from "@/context/searchContext";
import useSavedFilters from "@/hooks/useSavedFilters";
import { sendGtagEvent } from "@/lib/client/gtag";
import styles from "./styles.module.scss";

const MAX_SAVED_FILTERS = 20;

/** Filter groups that users actively chose (excludes defaults like sort/currency). */
const CHIP_GROUPS = [
  "brands",
  "type",
  "vendors",
  "regions",
  "continents",
  "size",
  "price",
] as const;

const chipLabel = (group: string, option: Filter) =>
  group === "price" ? "PRICE RANGE" : option.label;

/**
 * Saves the current search as a saved filter without the naming flow.
 * The name is derived from the search itself; signed-out users get the
 * sign-in modal instead.
 */
const useQuickSaveSearch = (source: string) => {
  const { user } = useUser();
  const { openSignIn } = useNativeSignIn();
  const { saveNewFilter, updateSavedFilter } = useSavedFilters();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const searchText = useFiltersStore((state) => state.searchText);
  const savedFilters = useFiltersStore((state) => state.savedFilters);
  const savedFilterId = useFiltersStore((state) => state.savedFilterId);
  const { saveFilters } = useFiltersStore((state) => state.actions);

  const deriveName = () => {
    if (searchText.trim()) return searchText.trim();
    const labels = CHIP_GROUPS.flatMap(
      (group) =>
        (activeFilters[group as keyof ActiveFilters] as Filter[] | undefined)
          ?.map((option) => chipLabel(group, option))
          .filter(Boolean) ?? []
    );
    return labels.slice(0, 3).join(" + ") || "My search";
  };

  const hasCustomSearch =
    !!searchText.trim() ||
    CHIP_GROUPS.some(
      (group) =>
        ((activeFilters[group as keyof ActiveFilters] as Filter[] | undefined) ?? []).length > 0
    );

  const save = async () => {
    if (!user) {
      void openSignIn();
      return;
    }
    if ((savedFilters?.length ?? 0) >= MAX_SAVED_FILTERS) {
      notifications.show({
        title: "Saved filter limit reached",
        message: `You've reached your limit of ${MAX_SAVED_FILTERS} saved filters. Delete one to save another.`,
        color: "yellow",
        autoClose: 5000,
      });
      return;
    }

    setSaving(true);
    try {
      await saveFilters(
        deriveName(),
        undefined,
        savedFilters || [],
        saveNewFilter,
        updateSavedFilter
      );
      setSaved(true);
      sendGtagEvent("click", "search", `save_filter_${source}`);
      // Success is communicated in place by the button switching to
      // "SEARCH SAVED" (see FeedEndCta / NoResultsRescue) rather than a toast.
    } catch (error) {
      notifications.show({
        title: "Could not save search",
        message: (error as Error).message || "Please try again.",
        color: "red",
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Already watching this search: via a selected saved filter, via this
  // session's save, or via a saved filter whose id matches activeFilters
  // (saveFilters stamps an id onto activeFilters, and that survives the
  // remount caused by the post-save reload while local state does not).
  const activeFiltersId = (activeFilters as ActiveFilters & { id?: number })?.id;
  const alreadyWatching =
    !!savedFilterId ||
    saved ||
    (!!activeFiltersId &&
      (savedFilters ?? []).some((f) => f.search_filter?.id === activeFiltersId));

  return { save, saving, saved, alreadyWatching, hasCustomSearch };
};

/**
 * Rendered under the "NO RESULTS FOUND" title. Turns a dead end into a
 * saved-search signup and offers one-click removal of active filters.
 */
export const NoResultsRescue = () => {
  const { removeFilter, updateKeywordSearch } = useSearchContext();
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const searchText = useFiltersStore((state) => state.searchText);
  const { save, saving, saved, alreadyWatching, hasCustomSearch } =
    useQuickSaveSearch("no_results");

  const chips = CHIP_GROUPS.flatMap((group) =>
    ((activeFilters[group as keyof ActiveFilters] as Filter[] | undefined) ?? []).map((option) => ({
      group: group as string,
      option,
    }))
  );

  return (
    <MantineBox className={styles.panel} data-testid="no-results-rescue">
      <MantineText className={styles.message}>
        {hasCustomSearch
          ? "KURB indexes new listings from resale stores all day. Save this search and we'll flag it when a match drops."
          : "KURB indexes new listings from resale stores all day. Check back soon."}
      </MantineText>
      {hasCustomSearch && (!alreadyWatching || saved) && (
        <Button
          variant="primary"
          size="md"
          onClick={save}
          loading={saving}
          disabled={saved}
          data-saved={saved}
        >
          {saved ? "SEARCH SAVED" : "SAVE THIS SEARCH"}
        </Button>
      )}
      {(chips.length > 0 || !!searchText.trim()) && (
        <MantineBox className={styles.chipsRow}>
          <MantineText span className={styles.chipsLabel}>
            Try removing:
          </MantineText>
          {searchText.trim() && (
            <MantineUnstyledButton
              type="button"
              className={styles.chip}
              onClick={() => updateKeywordSearch(undefined)}
            >
              <IconX size={13} />
              {searchText.trim()}
            </MantineUnstyledButton>
          )}
          {chips.map(({ group, option }) => (
            <MantineUnstyledButton
              key={`${group}-${option.value}-${option.label}`}
              type="button"
              className={styles.chip}
              onClick={() => removeFilter(option, group)}
            >
              <IconX size={13} />
              {chipLabel(group, option)}
            </MantineUnstyledButton>
          ))}
        </MantineBox>
      )}
    </MantineBox>
  );
};

/**
 * Rendered when the feed reaches its end. Marks the feed as genuinely done
 * (rather than stalled) and pitches the saved-search alert loop.
 */
export const FeedEndCta = () => {
  const { save, saving, saved, alreadyWatching, hasCustomSearch } = useQuickSaveSearch("feed_end");

  return (
    <MantineBox className={styles.panel} data-testid="feed-end-cta">
      <MantineText className={styles.caughtUpTitle}>YOU&apos;RE ALL CAUGHT UP</MantineText>
      {hasCustomSearch && (!alreadyWatching || saved) ? (
        <>
          <MantineText className={styles.message}>
            {saved
              ? "New matches will show up under your saved filters."
              : "Save this search and we'll flag new listings that match it."}
          </MantineText>
          <Button
            variant="primary"
            size="md"
            onClick={save}
            loading={saving}
            disabled={saved}
            data-saved={saved}
          >
            {saved ? "SEARCH SAVED" : "SAVE THIS SEARCH"}
          </Button>
        </>
      ) : (
        <MantineText className={styles.message}>
          New listings that match will show up here as vendors post them.
        </MantineText>
      )}
    </MantineBox>
  );
};
